import type { Session } from "next-auth";

import { isUserAdmin } from "@/lib/utils";

export const SAVED_LOGIN_ACCOUNTS_KEY = "saved_login_accounts";
export const MAX_SAVED_LOGIN_ACCOUNTS = 10;

export type SavedLoginAccount = {
	userId: string;
	idToken: string;
	email: string;
	firstName: string;
	lastName: string;
	avatar?: string;
	role?: string;
	user?: Record<string, unknown>;
	lastUsedAt: string;
	signedOut?: boolean;
};

function decodeJwtPayload(token: string): { exp?: number } | null {
	try {
		const parts = token.split(".");
		if (parts.length !== 3) return null;
		const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
		const payload = JSON.parse(atob(base64));
		return payload && typeof payload === "object" ? payload : null;
	} catch {
		return null;
	}
}

export function isJwtExpired(token: string): boolean {
	const payload = decodeJwtPayload(token);
	if (!payload?.exp) return false;
	return payload.exp * 1000 <= Date.now();
}

export function isSavedLoginAccountSignedOut(account: SavedLoginAccount): boolean {
	if (account.signedOut) return true;
	if (!account.idToken) return true;
	return isJwtExpired(account.idToken);
}

function getStorage() {
	if (typeof window === "undefined") return null;
	return window.localStorage;
}

function normalizeAccount(source: unknown): SavedLoginAccount | null {
	if (!source || typeof source !== "object") return null;

	const account = source as Partial<SavedLoginAccount>;
	const userId = String(account.userId ?? "");
	const idToken = String(account.idToken ?? "");
	const signedOut = Boolean(account.signedOut);
	if (!userId || (!idToken && !signedOut)) return null;

	return {
		userId,
		idToken,
		email: String(account.email ?? ""),
		firstName: String(account.firstName ?? ""),
		lastName: String(account.lastName ?? ""),
		avatar: account.avatar ? String(account.avatar) : undefined,
		role: account.role ? String(account.role) : undefined,
		user: account.user && typeof account.user === "object" ? account.user : undefined,
		lastUsedAt: account.lastUsedAt ? String(account.lastUsedAt) : new Date().toISOString(),
		signedOut: signedOut || undefined,
	};
}

function writeSavedLoginAccounts(accounts: SavedLoginAccount[]) {
	const storage = getStorage();
	if (!storage) return;

	storage.setItem(
		SAVED_LOGIN_ACCOUNTS_KEY,
		JSON.stringify(accounts.slice(0, MAX_SAVED_LOGIN_ACCOUNTS)),
	);
}

export function removeSavedLoginAccount(userId: string) {
	const accounts = getSavedLoginAccounts().filter((account) => account.userId !== userId);
	writeSavedLoginAccounts(accounts);
	return accounts;
}

export function markSavedLoginAccountSignedOut(userId: string) {
	const accounts = getSavedLoginAccounts();
	const index = accounts.findIndex((account) => account.userId === userId);
	if (index < 0) return accounts;

	accounts[index] = {
		...accounts[index],
		signedOut: true,
		idToken: "",
	};
	writeSavedLoginAccounts(accounts);
	return accounts;
}

export function getSavedLoginAccounts(): SavedLoginAccount[] {
	const storage = getStorage();
	if (!storage) return [];

	try {
		const raw = storage.getItem(SAVED_LOGIN_ACCOUNTS_KEY);
		if (!raw) return [];
		const decoded = JSON.parse(raw);
		if (!Array.isArray(decoded)) return [];
		return decoded
			.map(normalizeAccount)
			.filter((account): account is SavedLoginAccount => account !== null);
	} catch {
		return [];
	}
}

export function isSupportStaffAccount(account: Pick<SavedLoginAccount, "role">) {
	return isUserAdmin(account.role?.toUpperCase());
}

export function hasSavedSupportStaffAccount(accounts = getSavedLoginAccounts()) {
	return accounts.some(isSupportStaffAccount);
}

export function getSavedAccountDisplayName(account: Pick<SavedLoginAccount, "firstName" | "lastName" | "email">) {
	const first = account.firstName?.trim() ?? "";
	const last = account.lastName?.trim() ?? "";
	if (first.toLowerCase() === "user" && last === "-") return "User";
	const fullName = `${first} ${last}`.trim();
	return fullName || account.email;
}

export function canUseAccountSwitcher(session: Session | null, accounts = getSavedLoginAccounts()) {
	const currentRole = session?.user?.role;
	return isUserAdmin(currentRole?.toUpperCase()) || hasSavedSupportStaffAccount(accounts);
}

export function sessionToSavedLoginAccount(session: Session | null): SavedLoginAccount | null {
	const user = session?.user;
	const idToken = session?.idToken;
	if (!user?.id || !idToken) return null;

	return {
		userId: user.id,
		idToken,
		email: user.email ?? session.email ?? "",
		firstName: user.firstName ?? "",
		lastName: user.lastName ?? "",
		avatar: user.avatar,
		role: user.role,
		user: user as unknown as Record<string, unknown>,
		lastUsedAt: new Date().toISOString(),
		signedOut: false,
	};
}

export function saveCurrentAccountIfEligible(session: Session | null) {
	const account = sessionToSavedLoginAccount(session);
	if (!account) return false;

	const existingAccounts = getSavedLoginAccounts();
	if (!isSupportStaffAccount(account) && !hasSavedSupportStaffAccount(existingAccounts)) {
		return false;
	}

	return upsertSavedLoginAccount(account);
}

/** Always write a saved account entry (used after signup / explicit switcher saves). */
export function upsertSavedLoginAccount(account: SavedLoginAccount) {
	const existingAccounts = getSavedLoginAccounts();
	const nextAccounts = existingAccounts.filter((saved) => saved.userId !== account.userId);
	nextAccounts.unshift({
		...account,
		userId: String(account.userId),
		lastUsedAt: account.lastUsedAt || new Date().toISOString(),
		signedOut: false,
	});
	writeSavedLoginAccounts(nextAccounts);
	return true;
}

export function backendUserToSavedLoginAccount(
	user: Record<string, unknown>,
	idToken: string,
): SavedLoginAccount {
	const userId = String(user._id ?? user.id ?? "");
	return {
		userId,
		idToken,
		email: String(user.email ?? ""),
		firstName: String(user.firstName ?? ""),
		lastName: String(user.lastName ?? ""),
		avatar: user.avatar ? String(user.avatar) : undefined,
		role: user.role ? String(user.role) : undefined,
		user,
		lastUsedAt: new Date().toISOString(),
		signedOut: false,
	};
}

/** Snapshot the active session into the switcher before replacing the auth cookie. */
export async function preserveSessionForAccountSwitcher() {
	if (typeof window === "undefined") return false;
	const { getSession } = await import("next-auth/react");
	const session = await getSession();
	const { parkSessionForAccountSwitcher } = await import("@/lib/auth/switch-account");
	await parkSessionForAccountSwitcher(session);
	return saveCurrentAccountIfEligible(session);
}

export function moveSavedAccountToTop(userId: string) {
	const accounts = getSavedLoginAccounts();
	const index = accounts.findIndex((account) => account.userId === userId);
	if (index < 0) return accounts;

	const [selected] = accounts.splice(index, 1);
	accounts.unshift({
		...selected,
		lastUsedAt: new Date().toISOString(),
	});
	writeSavedLoginAccounts(accounts);
	return accounts;
}

export async function switchToNextSavedAccountAfterLogout(session: Session | null) {
	const currentUserId = session?.user?.id;
	if (!currentUserId) return false;

	let accounts = getSavedLoginAccounts();
	if (!accounts.some((account) => account.userId === currentUserId)) {
		return false;
	}

	accounts = removeSavedLoginAccount(currentUserId);
	for (const account of accounts) {
		const response = await fetch("/api/auth/create-session", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				idToken: account.idToken,
				user: account.user ?? {
					id: account.userId,
					email: account.email,
					firstName: account.firstName,
					lastName: account.lastName,
					avatar: account.avatar,
					role: account.role,
				},
			}),
		});

		if (response.ok) {
			moveSavedAccountToTop(account.userId);
			return true;
		}

		accounts = removeSavedLoginAccount(account.userId);
	}

	return false;
}
